import React from 'react'

import { X } from 'lucide-react'
import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Progress } from '~/client/src/shared/components/shadcn-ui/progress'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { ITilesetUploadState } from '~/client/src/shared/stores/domain/TilesetUploadProgress.store'

interface IProps {
  upload: ITilesetUploadState
  onCancel?: () => void
  onRetry?: () => void
}

@observer
export default class TilesetUploadCard extends React.Component<IProps> {
  private get progressPercentage(): number {
    const { upload } = this.props
    if (upload.fileSize === 0) return 0
    return upload.uploadedBytes / upload.fileSize
  }

  private get statusText(): string {
    const { upload } = this.props
    const { uploading, finishingUpload, uploadFailed } = Localization.translator
    switch (upload.status) {
      case 'uploading':
        return `${uploading}… ${Math.round(this.progressPercentage * 100)}%`
      case 'notifying-backend':
        return finishingUpload
      case 'error':
        return uploadFailed
      default:
        return ''
    }
  }

  private get secondaryText(): string | null {
    const { upload } = this.props
    const { pleaseWaitThisMayTakeAWhile, somethingWentWrongPleaseTryAgain } =
      Localization.translator
    if (upload.status === 'notifying-backend') {
      return pleaseWaitThisMayTakeAWhile
    }
    if (upload.status === 'error') {
      return upload.error || somethingWentWrongPleaseTryAgain
    }
    return null
  }

  public render(): React.ReactNode {
    const { upload, onCancel, onRetry } = this.props
    const isUploading =
      upload.status === 'uploading' || upload.status === 'notifying-backend'
    const isError = upload.status === 'error'

    return (
      <div className="tw:m-2.5 tw:rounded-[5px] tw:border tw:border-solid tw:border-border tw:p-[5px]">
        <div className="tw:flex tw:flex-col">
          <div className="tw:flex tw:items-center">
            <div className="tw:flex-1 tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:pl-[5px] tw:text-body-sm tw:text-foreground">
              {upload.fileName}
            </div>
            {isUploading && onCancel && (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={Localization.translator.cancel}
                onClick={onCancel}
              >
                <X />
              </Button>
            )}
          </div>
          <div className="tw:flex tw:items-center">
            <div
              className={`tw:mx-1.5 tw:size-1.5 tw:grow-0 tw:rounded-full ${
                isError ? 'tw:bg-destructive' : 'tw:bg-primary'
              }`}
            />
            <div className="tw:text-body-sm tw:text-foreground">
              {this.statusText}
            </div>
          </div>
          {this.secondaryText && (
            <div className="tw:pl-[18px] tw:text-body-sm tw:text-muted-foreground">
              {this.secondaryText}
            </div>
          )}
        </div>

        {isUploading && (
          <div className="tw:px-[5px] tw:py-[5px]">
            <Progress
              value={
                upload.status === 'uploading'
                  ? this.progressPercentage * 100
                  : null
              }
              variant="default"
            />
          </div>
        )}

        {isError && onRetry && (
          <button
            className="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:px-[5px] tw:py-[5px] tw:text-body-sm tw:text-primary"
            onClick={onRetry}
          >
            {Localization.translator.retry}
          </button>
        )}
      </div>
    )
  }
}
